import { CONTAINER_FIELD_REGISTRY } from './container-field-registry.mjs?v=20260806.100';
import { containerStatusLabel } from './container-status-presentation.mjs?v=20260806.100';
import { formatAuditTime } from './audit-utils.mjs?v=20260806.100';

/**
 * What somebody actually did, in a sentence a person can read.
 *
 * Alan, 2026-08-08: he screenshots these as evidence, and the people they are
 * shown to do not read databases. The panel was printing raw field paths against
 * raw values - one entry said only that `search_keywords` went from one array of
 * strings to the same array in a different order, and that `updated_at` moved by
 * four seconds. Nothing on screen said what the person had changed.
 *
 * Measured over 400 production audit entries the same day, the changed-field list
 * is mostly bookkeeping: updated_at on 319, search_keywords on 100,
 * source_workbook_sha256 on 97, import_fingerprint on 96, source_row on 96, and
 * every legacy_import_source.* key behind them. The business facts - remarks,
 * lfd_at, terminal_name, eta_at, ocean_status, container_no - are underneath.
 *
 * So the panel leads with the business changes written out, and everything the
 * system wrote to itself goes behind Technical details with a count. Nothing is
 * discarded: an audit trail that hides rows is worth less than one that is hard
 * to read, so the raw list is still one click away.
 */

const REGISTRY_LABELS = new Map(
  CONTAINER_FIELD_REGISTRY.map((field) => [field.key, field.label]),
);

// Fields the registry does not carry, either because they are not table columns
// or because they belong to another collection.
const EXTRA_LABELS = Object.freeze({
  status: '状态 Status',
  job_no: '任务号 Job No',
  remarks: '备注 Remarks',
  empty_return_note: '还空备注 Empty Return Note',
  due_at: '到期 Due',
  delivery_appointment_at: '送仓预约 Delivery Appointment',
  urgent_handle: '加急 Urgent',
  customer_paid: '客户已付 Customer Paid',
  ctf_status: 'CTF',
  tmf_status: 'TMF',
  driver_name: '司机 Driver',
  driver_id: '司机 Driver',
  name: '名称 Name',
  phone: '电话 Phone',
  email: '邮箱 Email',
  active: '启用 Active',
  role: '角色 Role',
  last_location_label: '位置 Location',
});

/**
 * Bookkeeping, not a business change. A predicate rather than a list, so a field
 * added next month behaves without anyone remembering this file: anything that is
 * a reference, an id, an internal index, a millisecond mirror of a date already
 * shown, or a trace of the import that wrote the row.
 */
const INTERNAL_EXACT = new Set([
  'updated_at', 'created_at', 'updated_by', 'created_by',
  'search_keywords', 'tenant_id', 'managed_by', 'archived_by_restore',
  'information_revision', 'last_workflow_audit_log_id', 'container_no_upper',
  'ocean_status_source', 'current_stage', 'is_past_due', 'is_empty_returned',
  'lfd_date_key', 'safe_projection', 'dashboard_state',
]);

const INTERNAL_SUFFIXES = ['_ref', '_uid', '_millis', '_key', '_sha256', '_fingerprint', '_id'];
const INTERNAL_PREFIXES = ['legacy_import_source', 'source_', 'import_', 'dashboard_dispatch_fields'];

export function isInternalAuditField(fieldPath) {
  const path = String(fieldPath ?? '');
  if (!path) return true;
  if (INTERNAL_EXACT.has(path)) return true;
  if (INTERNAL_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  // A dotted path is a nested write; the parent decides.
  const leaf = path.includes('.') ? path.split('.')[0] : path;
  if (INTERNAL_EXACT.has(leaf)) return true;
  // container_no and job_no are business identifiers even though they end in _no,
  // so only the id-shaped suffixes are excluded, and only when nothing else
  // claims the field.
  return INTERNAL_SUFFIXES.some((suffix) => path.endsWith(suffix))
    && !REGISTRY_LABELS.has(path) && !Object.hasOwn(EXTRA_LABELS, path);
}

/**
 * The registry labels are table headers and English, because that is what fits a
 * column. A sentence has room, and the people these screenshots are shown to read
 * Chinese, so the narrative names the field in Chinese where there is a word for
 * it (Alan, 2026-08-08). Anything not listed falls back to the header.
 */
const ZH_FIELD_LABELS = Object.freeze({
  customer_name: '客户', container_no: '柜号', master_bl_no: '提单号',
  size_type: '柜型', weight_lbs: '重量', ssl: '船司', terminal_name: '码头',
  ocean_status: '海运状态', status: '状态', eta_at: 'ETA', lfd_at: 'LFD',
  pickup_appointment_at: '提柜预约', delivery_appointment_at: '送仓预约',
  delivery_warehouse_address: '送货地址', pickup_driver_name: '提柜司机',
  return_driver_name: '还空司机', transfer_driver_name: '转仓司机',
  transfer_at: '转仓日期', chassis_no: '车架号', customer_chassis: '客户自带车架',
  pin: 'PIN', pre_alert_status: '预报状态', empty_container_no: '空柜号',
  reported_empty_at: '报空时间', return_empty_lfd_at: '还空LFD',
  actual_empty_returned_at: '实际还空时间', empty_returned_at: '还空时间',
  hold_status: 'HOLD', hold_note: '备注', customer_note: '客户备注', remarks: '备注',
  empty_return_note: '还空备注', job_no: '任务号', due_at: '到期',
  urgent_handle: '加急', customer_paid: '客户已付', ctf_status: 'CTF',
  tmf_status: 'TMF', empty_from_address: '空柜提取地址',
  empty_to_address: '空柜返回地址', pickup_company: '提柜公司', reported_empty_place: '报空地点',
});

export function auditFieldLabel(fieldPath, locale = 'en') {
  const path = String(fieldPath ?? '');
  if (String(locale).toLowerCase().startsWith('zh') && ZH_FIELD_LABELS[path]) {
    return ZH_FIELD_LABELS[path];
  }
  const label = REGISTRY_LABELS.get(path) ?? EXTRA_LABELS[path];
  if (label) return label;
  // Nothing claims it: show the path made readable rather than the raw key.
  return path.replaceAll('_', ' ').replace(/\b\w/gu, (character) => character.toUpperCase());
}

const STATUS_FIELDS = new Set(['status', 'ocean_status', 'current_stage']);

// Taken from the registry rather than listed by hand, so a column that changes
// format changes here with it.
const DATE_ONLY_FIELDS = new Set(
  CONTAINER_FIELD_REGISTRY.filter((field) => field.format === 'date-only').map((field) => field.key),
);

// The stored token is not the answer to the question the label asks - see
// CUSTOMER_CHASSIS_CHOICES in workspace-create-records.mjs. Reading it back
// wrong on an evidence screenshot would be worse than not showing it.
const CHASSIS_READINGS = Object.freeze({ own_chassis: '是 Yes', yes: '否 No', no: '否 No' });

function isTimestampish(value) {
  return Boolean(value) && (value.__type === 'timestamp' || typeof value?.toDate === 'function'
    || value instanceof Date || (typeof value === 'object' && Number.isFinite(value?.seconds)));
}

/**
 * A value written the way the table writes it: dates as dates, money as money,
 * a status as the word the operator knows it by.
 */
export function auditValueText(fieldPath, value, locale = 'en') {
  const zh = String(locale).toLowerCase().startsWith('zh');
  if (value === undefined || value === null
    || (value && typeof value === 'object' && value.__type === 'missing')
    || value === '') return zh ? '空白' : 'blank';
  if (value === '[REDACTED]' || value?.__type === 'redacted') return '[REDACTED]';
  const path = String(fieldPath ?? '');
  if (path === 'customer_chassis') return CHASSIS_READINGS[String(value)] ?? String(value);
  if (STATUS_FIELDS.has(path)) return containerStatusLabel(value, locale);
  if (typeof value === 'boolean') return value ? (zh ? '是' : 'Yes') : (zh ? '否' : 'No');
  if (path.endsWith('_cents') && Number.isFinite(Number(value))) {
    return new Intl.NumberFormat(zh ? 'zh-CN' : 'en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 2,
    }).format(Number(value) / 100);
  }
  // A clock time on a date-only field is noise that reads as precision nobody
  // recorded: LFD is a day, not 2:13:20 in the morning.
  if (isTimestampish(value)) {
    const text = formatAuditTime(value, locale);
    return DATE_ONLY_FIELDS.has(path) ? text.replace(/[,，]?\s*\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?$/iu, '') : text;
  }
  if (Array.isArray(value)) return value.length ? value.join('、') : (zh ? '空白' : 'blank');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const SENTENCES = Object.freeze({
  zh: Object.freeze({
    added: (label, after) => `填写了「${label}」：${after}`,
    changed: (label, before, after) => `「${label}」由 ${before} 改为 ${after}`,
    removed: (label, before) => `清空了「${label}」（原为 ${before}）`,
    created: (what) => `新建了${what}`,
    deleted: (what) => `删除了${what}`,
    noBusinessChange: '本次没有业务字段改动，只有系统内部记录。',
    technical: (count) => `另有 ${count} 项系统内部记录`,
  }),
  en: Object.freeze({
    added: (label, after) => `Set ${label} to ${after}`,
    changed: (label, before, after) => `Changed ${label} from ${before} to ${after}`,
    removed: (label, before) => `Cleared ${label} (was ${before})`,
    created: (what) => `Created ${what}`,
    deleted: (what) => `Deleted ${what}`,
    noBusinessChange: 'No business fields changed - only the system’s own bookkeeping.',
    technical: (count) => `${count} internal bookkeeping fields also changed`,
  }),
});

function sentencesFor(locale) {
  return String(locale).toLowerCase().startsWith('zh') ? SENTENCES.zh : SENTENCES.en;
}

/**
 * One change, one sentence. `changeType` is the audit log's own classification;
 * a "changed" whose before and after read identically is reported as a set
 * rather than as a change from a value to itself.
 */
export function narrateFieldChange(change, locale = 'en') {
  const copy = sentencesFor(locale);
  const label = auditFieldLabel(change?.fieldPath, locale);
  const before = auditValueText(change?.fieldPath, change?.before, locale);
  const after = auditValueText(change?.fieldPath, change?.after, locale);
  const blank = String(locale).toLowerCase().startsWith('zh') ? '空白' : 'blank';
  if (change?.changeType === 'removed' || after === blank) {
    return before === blank ? copy.added(label, after) : copy.removed(label, before);
  }
  if (change?.changeType === 'added' || before === blank || before === after) {
    return copy.added(label, after);
  }
  return copy.changed(label, before, after);
}

const ENTITY_WORDS = Object.freeze({
  zh: {
    containers: '柜子', shipments: '运单', customers: '客户', drivers: '司机',
    users: '用户', dispatch_tasks: '派车任务', terminals: '码头',
    finance_summaries: '财务记录',
  },
  en: {
    containers: 'a container', shipments: 'a shipment', customers: 'a customer',
    drivers: 'a driver', users: 'a user', dispatch_tasks: 'a dispatch task',
    terminals: 'a terminal', finance_summaries: 'a finance record',
  },
});

function entityWord(collection, locale) {
  const zh = String(locale).toLowerCase().startsWith('zh');
  const words = zh ? ENTITY_WORDS.zh : ENTITY_WORDS.en;
  return words[String(collection ?? '')] ?? (zh ? '记录' : 'a record');
}

/**
 * The whole entry told as sentences, plus how many bookkeeping fields were set
 * aside. Creation and deletion say so outright rather than listing every field
 * of a brand-new record as an individual change.
 */
export function narrateAuditEntry({
  changes = [], operation = 'updated', collection = '', identifier = '',
} = {}, locale = 'en') {
  const copy = sentencesFor(locale);
  const business = changes.filter((change) => !isInternalAuditField(change?.fieldPath));
  const internalCount = changes.length - business.length;
  const named = identifier ? `${entityWord(collection, locale)} ${identifier}` : entityWord(collection, locale);

  if (operation === 'created') return { lines: [copy.created(named)], internalCount };
  if (operation === 'deleted') return { lines: [copy.deleted(named)], internalCount };
  return {
    lines: business.length
      ? business.map((change) => narrateFieldChange(change, locale))
      : [copy.noBusinessChange],
    internalCount,
  };
}

export function technicalNoteText(count, locale = 'en') {
  return count > 0 ? sentencesFor(locale).technical(count) : '';
}
